import Client, {MASTER_IFRAME_NAME} from '@/hosted_fields/common/connection/client';
import {Master, IDeal, CardIconPosition} from '@/hosted_fields/common/enums';
import {
  FieldStatus,
  FieldValue,
  FontFace,
  ResponseInnerMessage,
  Styles,
  FieldComponentOptions,
  MessageData,
  FontsWhitelist,
  BroadcastStatus,
  Placeholder,
  Locale,
  ValidationStatus,
  AriaLabel,
  AllowedKeyPressEvents,
  TestCard,
  BINData,
  BinDataResponse,
} from '@/hosted_fields/common/types';
import {Events, Field, Card, HostedFields} from '@/hosted_fields/common/enums';
import BaseFactoryInterface from '@/hosted_fields/component/fields/base-factory-interface';
import {
  CardNumberTemplate,
  CardExpiryTemplate,
  CardCVVTemplate,
  FontTemplate,
  styleGenerator,
  getPlaceholderText,
  getAriaLabelText,
} from '@/hosted_fields/component/templates/card-templates';
import {clone, isEqual, toggleClass, deepAssign, has} from '@/hosted_fields/common/dom-utils';
import CardFormatter from '@/hosted_fields/component/card-formatter';
import Logger from '@/utils/logger_old';
import {CbKeyboardEvent} from '@/helpers/polyfills';
import {setCardIcon} from './card-icon-manager';
import {getBINData, validateVerificationRule} from '@/hosted_fields/common/utils';

const DEFAULT_COMPONENT_STATUS = {
  isValid: false,
  isInvalid: false,
  cardType: undefined,
  isComplete: false,
  isEmpty: true,
  error: '',
  forceValidation: false,
};

const helpers = {
  getFieldFromFieldType(fieldType: Card.ComponentFieldType | IDeal.ComponentFieldType): Field {
    switch (fieldType) {
      case Card.ComponentFieldType.Number:
        return Field.Number;
      case Card.ComponentFieldType.Expiry:
        return Field.Expiry;
      case Card.ComponentFieldType.CVV:
        return Field.CVV;
    }
  },

  postValidation(validation: ValidationStatus, {force = false, focus}): FieldStatus {
    const status: FieldStatus = {
      isComplete: validation.isComplete,
      isEmpty: validation.isEmpty,
      isValid: validation.isValid,
      isInvalid: force || (!validation.isEmpty && !focus) || validation.isComplete ? !validation.isValid : false,
      cardType: validation.cardType && validation.cardType.type,
      error: force || (!validation.isEmpty && !focus) || validation.isComplete ? validation.error : '',
    };
    return status;
  },
};

/*
  Common Method implementations for Individual Card Fields
*/
export default class BaseFactory implements BaseFactoryInterface {
  protected options: FieldComponentOptions;
  protected client: Client;
  protected formElement: HTMLFormElement;
  protected styleElement: HTMLStyleElement;
  protected inputElement: HTMLInputElement;
  protected status: FieldStatus = {...DEFAULT_COMPONENT_STATUS};
  protected previousValue: string = '';
  protected fieldType: Card.ComponentFieldType | IDeal.ComponentFieldType;
  protected isFocused: boolean = false;
  private _id: string;
  private cardType: object = null;
  private fonts: Array<FontFace>;
  private fontWhitelist: string[] = [];
  // Flag - to indicate a forced validation
  private forceFlag: boolean = false;
  private gwConfig: any = {};
  private selectedScheme: string = undefined;
  private binData: BinDataResponse = undefined;

  private iconEl: HTMLDivElement;
  private iconImages: (SVGElement | HTMLImageElement)[] = [];
  private iconImageParent: HTMLDivElement;

  constructor(client: Client, data: MessageData) {
    this.client = client;
    this.options = {
      field: data.options,
      component: data.baseOptions,
      cardIconPosition: CardIconPosition.END,
    };
    this.fonts = [];
    this.fieldType = data.fieldType;
    this._id = helpers.getFieldFromFieldType(this.fieldType);
  }
  selectTestCard(eventData: {data: {selectedCardData: TestCard}}): ResponseInnerMessage {
    const {data} = eventData || {};
    if (!data || !data.selectedCardData) {
      return {
        acknowledged: true,
      };
    }
    const card = data.selectedCardData;
    switch (this._id) {
      case Field.Number:
        this.inputElement.value = card.number;
        break;
      case Field.Expiry:
        const exp = `${card.exp_month} / ${card.exp_year.slice(-2)}`;
        this.inputElement.value = exp;
        break;
      case Field.CVV:
        this.inputElement.value = card.cvv;
        break;
    }
    let keyUpEvent = CbKeyboardEvent('keyup');

    // To trigger on change
    this.inputElement.dispatchEvent(keyUpEvent);
    return {
      acknowledged: true,
    };
  }

  configData(data: any): ResponseInnerMessage {
    this.gwConfig = data.gwConfig;
    return {
      acknowledged: true,
    };
  }

  create() {
    return new Promise((resolve, reject) => {
      Logger.init();

      // Generate iframe body content
      document.body.innerHTML = this.generateHTMLTemplate(this.options);

      this.iconEl = document.querySelector('div.CardIcon');
      this.iconImages = [<SVGElement>document.querySelector('#CardIcon-svg')].filter(Boolean);
      this.iconImageParent = <HTMLDivElement>document.querySelector('#CardIcon-parent');

      this.addListeners(this._id);
      resolve({loaded: true});
    });
  }

  // Embed fonts in DOM
  embedFonts() {
    let fontTemplate = FontTemplate(this.fonts || [], this.fontWhitelist);
    if (fontTemplate) {
      // !TODO check cross browser support
      // !TODO CSP check
      document.head.insertAdjacentHTML('beforeend', fontTemplate);
    }
  }

  destroy() {
    this.removeListeners(this._id);

    // Remove Inputs from DOM
    this.inputElement.remove();
    delete this.inputElement;

    // Remove styles from DOM
    this.styleElement.remove();
    delete this.styleElement;

    // Remove Form
    this.formElement.remove();
    delete this.formElement;

    return {
      acknowledged: true,
    };
  }

  preventKeys(event: KeyboardEvent) {
    // Prevent Enter key form submit
    if (event.keyCode == 13) {
      event.preventDefault();
      return false;
    }
  }

  onKeyPress(event: KeyboardEvent) {
    if (has(AllowedKeyPressEvents, event.key)) {
      const message = {
        action: Master.Actions.RelayEvent,
        data: {
          event: Events.keyPress,
          key: event.key,
          frame: window.name,
          fieldName: (event.target as HTMLInputElement).getAttribute('name'),
        },
      };
      this.client.sendMessage(message, MASTER_IFRAME_NAME);
    }
  }

  addListeners(id: string) {
    const element = <HTMLInputElement>document.getElementById(id);

    this.inputElement = element;
    this.formElement = <HTMLFormElement>document.querySelector('form.CardForm');
    this.styleElement = <HTMLStyleElement>document.querySelector('form.CardForm style');

    element.addEventListener('focus', this.onFocus.bind(this));
    element.addEventListener('blur', this.onBlur.bind(this));
    element.addEventListener('keydown', this.preventKeys.bind(this));
    element.addEventListener('keydown', this.onKeyPress.bind(this));
    element.addEventListener('keyup', this.onChange.bind(this));
  }

  blur(): ResponseInnerMessage {
    if (this.inputElement) this.inputElement.blur();
    return {
      acknowledged: true,
    };
  }

  focus(): ResponseInnerMessage {
    if (this.inputElement) this.inputElement.focus();
    return {
      acknowledged: true,
    };
  }

  clear(): ResponseInnerMessage {
    this.inputElement.value = '';
    let keyUpEvent = CbKeyboardEvent('keyup');

    // To trigger on change
    this.inputElement.dispatchEvent(keyUpEvent);

    return {
      acknowledged: true,
    };
  }

  onFocus(event: FocusEvent) {
    const message = {
      action: Master.Actions.RelayEvent,
      data: {
        event: Events.focus,
        frame: window.name,
      },
    };
    this.isFocused = true;
    toggleClass(this.formElement, HostedFields.InternalCSSClass.focus, true);
    toggleClass(this.inputElement, HostedFields.InternalCSSClass.focus, true);
    this.client.sendMessage(message, MASTER_IFRAME_NAME);
  }

  onBlur(event: FocusEvent) {
    const message = {
      action: Master.Actions.RelayEvent,
      data: {
        event: Events.blur,
        frame: window.name,
      },
    };
    this.isFocused = false;
    toggleClass(this.formElement, HostedFields.InternalCSSClass.focus, false);
    toggleClass(this.inputElement, HostedFields.InternalCSSClass.focus, false);

    this.client.sendMessage(message, MASTER_IFRAME_NAME);
    //@ts-ignore Passing FocusEvent to KeyboardEvent type
    this.onChange(event);
  }

  removeListeners(id: string) {
    const element = document.getElementById(id);
    // TODO change this. It will not work
    element.removeEventListener('focus', this.onFocus);
    element.removeEventListener('blur', this.onBlur);
    element.removeEventListener('keyup', this.onChange);
  }

  getValue(): FieldValue {
    const id = this.inputElement.id;
    let resp = {
      [id]: {
        value: (<HTMLInputElement>document.getElementById(id)).value,
        ...this.status,
      },
    };
    if (id === Field.Number) {
      resp = {...resp, [Field.PREFERRED_NETWORK]: {value: this.selectedScheme}};
    }
    return resp;
  }

  updateStyles(styles: Styles): ResponseInnerMessage {
    deepAssign(this.options.component[HostedFields.Options.style], styles);
    deepAssign(this.options.field[HostedFields.Options.style], styles);
    let newStyleHTML: string = styleGenerator(this.options);

    if (this.styleElement) {
      this.styleElement.insertAdjacentHTML('afterend', newStyleHTML);

      // Remove existing style element from DOM
      this.styleElement.remove();

      // Reassign new style element
      this.styleElement = <HTMLStyleElement>document.querySelector('form.CardForm style');
    }
    return {acknowledged: true};
  }

  generateHTMLTemplate(options: FieldComponentOptions): string {
    switch (this._id) {
      case Field.Number:
        return CardNumberTemplate(options);
      case Field.Expiry:
        return CardExpiryTemplate(options);
      case Field.CVV:
        return CardCVVTemplate(options);
    }
  }

  onChange(event: KeyboardEvent) {
    // Skip onchange on tab/shift+tab
    if (event.keyCode === 9 || event.keyCode === 16 || event.key == 'Tab' || event.key == 'Shift') return;

    const target = <HTMLInputElement>event.target;
    const value = target.value;
    const prevStatus = {...this.status};
    this.status.forceValidation = value == this.previousValue && this.forceFlag;

    this.validate(target, this.status.forceValidation);
    const currentStatus = {...this.status};
    const stateChanged = !isEqual(prevStatus, currentStatus);

    // Communicate status to Iframes & HOST
    if (stateChanged || this.status.forceValidation) {
      this.relayStatus();
      this.setCSSClasses();
    }

    this.forceFlag = false;
    this.status.forceValidation = false;
    this.previousValue = value;
  }

  private animateCardIcon() {
    // Card icon load animation
    toggleClass(this.iconEl, 'CardIcon--loaded', false);
    setTimeout(() => {
      toggleClass(this.iconEl, 'CardIcon--loaded', true);
    }, 50);
  }

  setSelectedScheme = (scheme: string) => {
    this.selectedScheme = scheme;
  };

  setBinData = (binData: BinDataResponse | undefined) => {
    this.binData = binData;
  };

  private setCardIcon(target: any, prevStatus: FieldStatus) {
    if (this._id === Field.Number) {
      const isInvalid = this.status.isInvalid;
      const cardType = this.status.cardType;
      const prevCardType = prevStatus.cardType;
      const prevInvalid = prevStatus.isInvalid;

      setCardIcon(
        target,
        isInvalid,
        target.value,
        cardType,
        prevCardType,
        prevInvalid,
        this.client,
        this.gwConfig,
        this.iconImageParent,
        this.iconImages,
        this.setSelectedScheme,
        () => this.animateCardIcon,
        this.previousValue,
        this.setBinData
      );
    }
  }

  getBINData(value: string): BINData | undefined {
    return getBINData(value, this.binData);
  }

  validate(target: HTMLInputElement, force?: boolean) {
    const value = target.value;
    let validation;
    switch (this._id) {
      case Field.Number:
        validation = CardFormatter.validateCardNumber(value);
        break;
      case Field.Expiry:
        validation = CardFormatter.validateCardExpiry(value);
        break;
      case Field.CVV:
        validation = CardFormatter.validateCardCVC(
          value,
          this.cardType,
          this.options.component.field[HostedFields.Field.cvv]
        );
        break;
    }

    if (this._id === Field.Number && validation.isValid) {
      this.validateVerificationRule(validation);
    }

    const updatedStatus: FieldStatus = helpers.postValidation(validation, {
      force,
      focus: this.isFocused,
    });

    if (this._id === Field.Number && updatedStatus.isValid && updatedStatus.cardType) {
      // Attach BIN Data for cards
      const binData = this.getBINData(value);
      if (binData) updatedStatus.binData = binData;
    }

    const prevStatus: FieldStatus = clone(this.status);
    this.status = {
      ...this.status,
      ...updatedStatus,
    };

    this.setCardIcon(target, prevStatus);
  }

  private validateVerificationRule(validation: ValidationStatus): void {
    validateVerificationRule(validation, this.binData);
  }

  setCSSClasses() {
    const status = this.status;
    const isInvalid =
      status.forceValidation || (!status.isEmpty && (!this.isFocused || status.isComplete)) ? !status.isValid : false;

    toggleClass(this.inputElement, HostedFields.InternalCSSClass.empty, status.isEmpty);
    toggleClass(this.inputElement, HostedFields.InternalCSSClass.invalid, isInvalid);
    toggleClass(this.inputElement, HostedFields.InternalCSSClass.valid, status.isValid);
    toggleClass(this.inputElement, HostedFields.InternalCSSClass.complete, status.isComplete);

    toggleClass(this.formElement, HostedFields.InternalCSSClass.empty, status.isEmpty);
    toggleClass(this.formElement, HostedFields.InternalCSSClass.invalid, isInvalid);
    toggleClass(this.formElement, HostedFields.InternalCSSClass.valid, status.isValid);
    toggleClass(this.formElement, HostedFields.InternalCSSClass.complete, status.isComplete);
  }

  validateField() {
    this.forceFlag = true;
    let keyUpEvent = CbKeyboardEvent('keyup');

    // To trigger on change
    this.inputElement.dispatchEvent(keyUpEvent);
    return {
      [this._id]: clone(this.status),
    };
  }

  relayStatus() {
    const message = {
      action: Master.Actions.Broadcast,
      data: {
        event: Events.change,
        frame: window.name,
        status: {
          [this._id]: clone(this.status),
        },
      },
    };
    this.client.sendMessage(message, MASTER_IFRAME_NAME);
  }

  // Onchange Callback for other fields
  receiveStatus(statusEvent: BroadcastStatus): ResponseInnerMessage {
    let currentField = helpers.getFieldFromFieldType(this.fieldType);
    if (currentField == Field.CVV && statusEvent.status && statusEvent.status[Field.Number]) {
      const cc_number: FieldStatus = statusEvent.status[Field.Number];
      this.cardType = cc_number.cardType;

      // Retrigger validation for CVV
      if (this.previousValue.trim()) this.validateField();
    }

    return {
      elementId: this.inputElement.id,
      acknowledged: true,
    };
  }

  updateFonts(data: FontsWhitelist): ResponseInnerMessage {
    this.fonts = data.fonts;
    this.fontWhitelist = data.whitelist;
    // embed fonts
    this.embedFonts();
    return {
      acknowledged: true,
    };
  }

  updatePlaceholder(data: Placeholder | string): ResponseInnerMessage {
    let placeholder = this.inputElement.placeholder;
    if (data) {
      if (typeof data === 'object') placeholder = data[this.fieldType] || placeholder;
      else if (typeof data === 'string') placeholder = data || placeholder;
    }
    this.inputElement.placeholder = placeholder;
    return {acknowledged: true};
  }

  updateAriaLabel?(data: AriaLabel | string): ResponseInnerMessage {
    let ariaLabel = this.inputElement.getAttribute('aria-label');
    if (data) {
      if (typeof data === 'object') ariaLabel = data[this.fieldType] || ariaLabel;
      else if (typeof data === 'string') ariaLabel = data || ariaLabel;
    }

    this.inputElement.setAttribute('aria-label', ariaLabel);
    return {acknowledged: true};
  }

  updateIcon(data: boolean): ResponseInnerMessage {
    let icon = data;
    this.options.component[HostedFields.Options.icon] = icon;
    document.body.innerHTML = this.generateHTMLTemplate(this.options);
    this.addListeners(this._id);
    return {acknowledged: true};
  }

  updateLocale(data: Locale): ResponseInnerMessage {
    if (this.options.component) {
      this.options.component[HostedFields.Options.locale] = data;
      this.inputElement.placeholder = getPlaceholderText(this.options, this.fieldType);
      this.inputElement.setAttribute('aria-label', getAriaLabelText(this.options, this.fieldType));
    }
    return {acknowledged: true};
  }

  updateIDealBankList(data: any): ResponseInnerMessage {
    return {acknowledged: true};
  }

  iDealBankSelected(data: any): ResponseInnerMessage {
    return {acknowledged: true};
  }
}
